import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import JMuxer from 'jmuxer';
import { getProfilePicUrl, getLocalProfilePicUrl } from '../../config/imageUrls';
import {
  Monitor, X, Play, Pause, RefreshCw, Smartphone, AlertCircle,
  ChevronLeft, Home, LayoutGrid, Bell, RotateCcw, ChevronDown, ChevronUp,
  Battery, BatteryCharging, Thermometer, Cpu, HardDrive,
  ArrowDown, ArrowUp, Square, Type, Send, Wifi, Fingerprint, Zap,
  Maximize2, Minimize2, Plane
} from 'lucide-react';

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const Panel = styled.div`
  width: ${p => p.$open ? (p.$wide ? '520px' : '320px') : '0'};
  min-width: ${p => p.$open ? (p.$wide ? '520px' : '320px') : '0'};
  height: 100vh;
  position: fixed;
  right: ${p => p.$rightOffset || '0'}; top: 0;
  background: var(--bg);
  border-left: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  z-index: 900;
  transition: width .25s ease, min-width .25s ease, right .25s ease;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
`;

const PanelTitle = styled.div`
  font-size: 12px; font-weight: 700; color: var(--text-primary);
  display: flex; align-items: center; gap: 6px; flex: 1;
`;

const HeaderBtn = styled.button`
  width: 26px; height: 26px; border-radius: 6px; border: none;
  background: ${p => p.$active ? 'var(--primary)' : 'transparent'};
  color: ${p => p.$active ? '#fff' : 'var(--text-muted)'};
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all .15s;
  &:hover { background: ${p => p.$active ? 'var(--primary)' : 'var(--surface)'}; color: ${p => p.$active ? '#fff' : 'var(--primary)'}; }
`;

/* ── ScreenArea: flex:1 when collapsed so it grows ── */
const ScreenArea = styled.div`
  flex: ${p => p.$expanded ? '0 0 auto' : '1'};
  display: flex; flex-direction: row; align-items: center;
  justify-content: center;
  padding: 8px 4px 12px 12px;
  background: #000;
  position: relative;
  transition: flex 0.35s ease;
  min-height: 0;
`;

const ScreenColumn = styled.div`
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  min-height: 0;
`;

const ScrollColumn = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  margin-left: 4px; flex-shrink: 0;
`;

const ScrollBtn = styled.button`
  width: 26px; height: 40px; border-radius: 6px; border: none;
  background: rgba(165,145,239,.15); color: rgba(165,145,239,.7);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all .15s;
  &:hover { background: rgba(165,145,239,.3); color: #fff; }
  &:active { transform: scale(.93); }
`;

const PhoneFrame = styled.div`
  width: 100%; max-width: ${p => p.$wide ? '420px' : '240px'};
  aspect-ratio: 9/19.5;
  max-height: calc(100vh - 200px);
  border-radius: 20px; overflow: hidden; position: relative;
  border: 2px solid rgba(165,145,239,.25);
  background: #111;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 1;
  min-height: 0;
  z-index: 1;
`;

const ScreenImg = styled.img`
  width: 100%; height: 100%; object-fit: contain;
  cursor: crosshair;
  user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;
`;

const Placeholder = styled.div`
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; color: #888; gap: 10px;
  text-align: center; padding: 20px; position: absolute; inset: 0;
  svg { opacity: .3; }
  span { font-size: 11px; line-height: 1.4; }
`;

/* ── Control panel ── */
const ControlPanel = styled.div`
  flex: ${p => p.$expanded ? '1' : '0 0 auto'};
  background: var(--bg);
  border-top-left-radius: 18px;
  border-top-right-radius: 18px;
  margin-top: -14px;
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  overflow-y: ${p => p.$expanded ? 'auto' : 'hidden'};
  overflow-x: hidden;
  border-top: 1px solid var(--border);
  transition: flex 0.35s ease;

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;

/* ── Always-visible top section: device name + nav buttons ── */
const ControlTop = styled.div`
  flex-shrink: 0;
  padding: 10px 14px 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DeviceRow = styled.div`
  display: flex; align-items: center; gap: 8px;
`;

const DeviceIdentity = styled.div`
  flex: 1; min-width: 0; display: flex; align-items: center; gap: 8px;
`;

const ProfilePic = styled.img`
  width: 28px; height: 28px; border-radius: 50%; object-fit: cover;
  flex-shrink: 0; border: 2px solid var(--primary);
`;

const DeviceInfo = styled.div`
  flex: 1; min-width: 0;
`;

const DeviceNameSmall = styled.div`
  font-size: 12px; font-weight: 700; color: var(--text-primary);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const AccountNameSmall = styled.div`
  font-size: 10px; color: var(--primary); font-weight: 600;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const StatusBadge = styled.span`
  font-size: 9px; font-weight: 700; padding: 2px 8px; border-radius: 10px;
  background: ${p => p.$on ? 'rgba(34,197,94,.12)' : 'rgba(239,68,68,.12)'};
  color: ${p => p.$on ? '#22c55e' : '#ef4444'};
  white-space: nowrap; flex-shrink: 0;
`;

const ActionBtns = styled.div`
  display: flex; gap: 4px; flex-shrink: 0;
`;

const ActionBtn = styled.button`
  width: 28px; height: 28px; border-radius: 7px; border: none;
  background: ${p => p.$color || 'var(--surface)'};
  color: ${p => p.$iconColor || 'var(--text-muted)'};
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all .15s; flex-shrink: 0;
  &:hover { background: ${p => p.$hoverBg || 'var(--primary)'}; color: #fff; }
  &:active { transform: scale(.93); }
  &:disabled { opacity: .4; cursor: not-allowed; }
`;

/* ── Text input bar ── */
const TextInputBar = styled.div`
  display: flex; align-items: center; gap: 4px;
  padding: 0 14px 6px;
`;

const TextInputField = styled.input`
  flex: 1; height: 30px; border-radius: 7px;
  border: 1px solid var(--border); background: var(--surface);
  color: var(--text-primary); font-size: 11px; padding: 0 10px;
  outline: none;
  &:focus { border-color: var(--primary); }
`;

const TextSendBtn = styled.button`
  width: 30px; height: 30px; border-radius: 7px; border: none;
  background: var(--primary); color: #fff;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all .15s; flex-shrink: 0;
  &:hover { opacity: .85; }
  &:active { transform: scale(.93); }
`;

/* ── Nav buttons (always visible) ── */
const NavSection = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 3px;
`;

const NavBtn = styled.button`
  flex: 1; height: 28px; min-width: 0; border-radius: 6px; border: none;
  background: var(--surface); color: var(--text-muted); cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all .15s; padding: 0;
  &:hover { background: var(--primary); color: #fff; }
  &:active { transform: scale(.93); }
`;

const RestartBtn = styled(NavBtn)`
  &:hover { background: var(--error); color: #fff; }
`;

/* ── Expand toggle ── */
const ExpandToggle = styled.button`
  width: 100%;
  padding: 5px 14px;
  background: transparent;
  border: none;
  border-top: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: var(--text-muted);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: var(--surface);
    color: var(--primary);
  }
`;

/* ── Expandable details section ── */
const DetailsSection = styled.div`
  padding: 0 14px 8px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/* ── Info row for details section ── */
const InfoRow = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 6px 10px;
  background: var(--surface);
  border-radius: 8px;
  font-size: 11px;
`;

const InfoIcon = styled.div`
  width: 24px; height: 24px; border-radius: 6px;
  background: ${p => p.$bg || 'rgba(165,145,239,.1)'};
  display: flex; align-items: center; justify-content: center;
  color: ${p => p.$color || 'var(--primary)'}; flex-shrink: 0;
`;

const InfoText = styled.div`
  flex: 1; min-width: 0;
`;

const InfoLabel = styled.div`
  font-size: 8px; color: var(--text-muted); text-transform: uppercase;
  font-weight: 700; letter-spacing: .3px;
`;

const InfoValue = styled.div`
  font-size: 11px; color: var(--text-primary); font-weight: 600;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

/* ── Stats grid ── */
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
`;

const StatCard = styled.div`
  padding: 8px 10px;
  background: var(--surface);
  border-radius: 8px;
  display: flex; align-items: center; gap: 8px;
`;

const StatIcon = styled.div`
  width: 28px; height: 28px; border-radius: 7px;
  background: ${p => p.$color || 'rgba(165,145,239,.1)'};
  display: flex; align-items: center; justify-content: center;
  color: ${p => p.$iconColor || 'var(--primary)'};
  flex-shrink: 0;
`;

const StatInfo = styled.div`
  min-width: 0;
`;

const StatValue = styled.div`
  font-size: 13px; font-weight: 700; color: var(--text-primary);
  line-height: 1.2;
`;

const StatLabel = styled.div`
  font-size: 9px; color: var(--text-muted); text-transform: uppercase;
  font-weight: 600; letter-spacing: .3px;
`;

/* ── Status bar ── */
const StatusBar = styled.div`
  padding: 6px 14px; border-top: 1px solid var(--border);
  font-size: 10px; color: var(--text-muted); display: flex;
  align-items: center; justify-content: space-between; flex-shrink: 0;
  background: var(--bg);
`;

const StatusDot = styled.span`
  display: inline-block; width: 6px; height: 6px; border-radius: 50%;
  background: ${p => p.$on ? 'var(--success)' : 'var(--error)'};
  margin-right: 6px;
`;

/* ═══════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════ */

const STATS_POLL_INTERVAL = 5000;
const LONG_PRESS_MS = 500;       // Threshold for long-press detection
const SWIPE_MIN_DIST = 30;       // Minimum px drag to count as swipe

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */

const ScreenMirrorPanel = ({ isOpen, onClose, selectedDevice, rightOffset, onWideModeChange }) => {
  const [streaming, setStreaming] = useState(true);
  const [hasFrame, setHasFrame] = useState(false);
  const [error, setError] = useState(null);
  const [fps, setFps] = useState(0);
  const [deviceStats, setDeviceStats] = useState(null);
  const [panelExpanded, setPanelExpanded] = useState(true);
  const [showTextInput, setShowTextInput] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [stopping, setStopping] = useState(false);
  const [mode, setMode] = useState('video'); // 'video' (fast poll) or 'screenshot' (slow poll) — only used in fallback
  const [streamMode, setStreamMode] = useState('live'); // 'live' (scrcpy H.264) or 'fallback' (PNG polling)
  const [liveConnected, setLiveConnected] = useState(false);
  const [wideMode, setWideMode] = useState(false);
  const [pollIntervalMs, setPollIntervalMs] = useState(() => {
    const saved = localStorage.getItem('mirrorPollInterval');
    return saved ? parseInt(saved) : 350;
  });

  const statsTimerRef = useRef(null);
  const imgRef = useRef(null);
  const videoRef = useRef(null);
  const phoneFrameRef = useRef(null);
  const frameCountRef = useRef(0);
  const mountedRef = useRef(true);
  const [screenRes, setScreenRes] = useState({ w: 720, h: 1520 });

  // Touch/gesture refs
  const pointerDownRef = useRef(null);
  const longPressTimerRef = useRef(null);

  // Polling refs (fallback mode only)
  const fetchingRef = useRef(false);
  const errorCountRef = useRef(0);
  const pollTimerRef = useRef(null);
  const commandPauseRef = useRef(false); // pause polling during ADB commands

  // Scrcpy live refs
  const wsRef = useRef(null);
  const jmuxerRef = useRef(null);
  const liveConnectedRef = useRef(false);

  const deviceId = selectedDevice?.device_id;
  const deviceName = selectedDevice?.device_name || selectedDevice?.model || deviceId || '—';
  const accountUsername = selectedDevice?.instagram_username || selectedDevice?.username || selectedDevice?.account_username || null;
  const profilePicUrl = accountUsername
    ? getLocalProfilePicUrl(accountUsername)
    : null;
  const serverPicUrl = accountUsername
    ? getProfilePicUrl(accountUsername)
    : null;
  const [fetchedIp, setFetchedIp] = useState(null);
  const deviceIp = selectedDevice?.ip_address || selectedDevice?.ip || fetchedIp;

  // ─── Helper: send an ADB input command via HTTP ───
  // Pauses screenshot polling to avoid ADB serial lock contention
  const adbCommand = useCallback(async (endpoint, body = {}) => {
    if (!deviceId) return;
    commandPauseRef.current = true;
    try {
      const r = await fetch(`/api/devices/${deviceId}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.status === 404) {
        console.warn(`[ScreenMirror] ${endpoint}: device not found`);
        setError('Device déconnecté');
        if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
        setStreaming(false);
        return;
      }
      if (!r.ok) console.warn(`[ScreenMirror] ${endpoint} failed: HTTP ${r.status}`);
    } catch (e) {
      console.warn(`[ScreenMirror] ${endpoint} error:`, e.message);
    } finally {
      // Resume polling after ADB command finishes + small cool-down
      // ADB needs ~100ms to release the device serial lock after a shell command
      setTimeout(() => { commandPauseRef.current = false; }, 100);
    }
  }, [deviceId]);

  // ─── Helper: map pointer coords to device coords ───
  const pointerToDevice = useCallback((clientX, clientY, clamp = false) => {
    const el = phoneFrameRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    let rx = (clientX - rect.left) / rect.width;
    let ry = (clientY - rect.top) / rect.height;
    if (!clamp && (rx < 0 || rx > 1 || ry < 0 || ry > 1)) return null;
    rx = Math.max(0, Math.min(1, rx));
    ry = Math.max(0, Math.min(1, ry));
    return { x: Math.round(rx * screenRes.w), y: Math.round(ry * screenRes.h) };
  }, [screenRes]);

  // Fetch device IP + screen resolution on mount / device change
  useEffect(() => {
    if (!deviceId) { setFetchedIp(null); return; }
    let cancelled = false;
    fetch('/api/phone-cast/get-ip', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ device_id: deviceId })
    }).then(r => r.json()).then(d => {
      if (!cancelled && d.success && d.ip && d.ip !== 'N/A') setFetchedIp(d.ip);
    }).catch(() => {});
    // Fetch actual screen resolution for accurate touch mapping
    fetch(`/api/devices/${deviceId}/info`)
      .then(r => r.json())
      .then(d => {
        if (!cancelled && d.success && d.screen) {
          setScreenRes({ w: d.screen.width, h: d.screen.height });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [deviceId]);

  // ════════════════════════════════════════════════
  // SCRCPY LIVE STREAMING (WebSocket + JMuxer H.264)
  // ════════════════════════════════════════════════

  const initJMuxer = useCallback(() => {
    if (jmuxerRef.current) {
      try { jmuxerRef.current.destroy(); } catch (_) {}
    }
    const videoEl = videoRef.current;
    if (!videoEl) return;
    jmuxerRef.current = new JMuxer({
      node: videoEl,
      mode: 'video',
      flushingTime: 0,
      maxDelay: 500,
      fps: 30,
      clearBuffer: true,
      debug: false,
      onReady: () => console.log('[scrcpy-live] JMuxer ready (MSE/SourceBuffer open)'),
      onError: (err) => console.error('[scrcpy-live] JMuxer error:', err),
    });
    // Monitor the video element for playback issues
    videoEl.onloadeddata = () => console.log('[scrcpy-live] Video: loadeddata event');
    videoEl.onplay = () => console.log('[scrcpy-live] Video: play event');
    videoEl.onerror = (e) => console.error('[scrcpy-live] Video error:', videoEl.error);
  }, []);

  const connectScrcpy = useCallback(() => {
    if (!deviceId) return;
    if (wsRef.current && wsRef.current.readyState <= 1) return; // already open/connecting

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.hostname}:3002/ws/scrcpy-live/${deviceId}`;
    console.log(`🔗 [scrcpy-live] Connecting to ${wsUrl}`);

    const ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = () => {
      console.log(`✅ [scrcpy-live] WebSocket opened for ${deviceId}`);
    };

    ws.onmessage = (event) => {
      if (typeof event.data === 'string') {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'init') {
            console.log(`🎥 [scrcpy-live] Init: ${msg.width}x${msg.height} ${msg.deviceName}`);
            setScreenRes({ w: msg.width, h: msg.height });
            setLiveConnected(true);
            liveConnectedRef.current = true;
            setHasFrame(true);
            setError(null);
            initJMuxer();
          } else if (msg.type === 'error') {
            console.warn(`⚠️ [scrcpy-live] Error: ${msg.message} — falling back to PNG`);
            setLiveConnected(false);
            liveConnectedRef.current = false;
            setStreamMode('fallback');
          } else if (msg.type === 'disconnected') {
            setLiveConnected(false);
            liveConnectedRef.current = false;
          }
        } catch (_) {}
      } else {
        // Binary H.264: [flags(1B)] [reserved(3B)] [H.264 packet...]
        const data = new Uint8Array(event.data);
        if (data.length < 5) return;
        const h264Data = data.slice(4);
        if (jmuxerRef.current) {
          try { jmuxerRef.current.feed({ video: h264Data, duration: 0 }); } catch (_) {}
        }
        frameCountRef.current++;
      }
    };

    ws.onclose = () => {
      console.log(`🔴 [scrcpy-live] WebSocket closed for ${deviceId}`);
      wsRef.current = null;
      if (liveConnectedRef.current) {
        setLiveConnected(false);
        liveConnectedRef.current = false;
      }
    };

    ws.onerror = () => {
      console.warn(`⚠️ [scrcpy-live] WebSocket error — falling back to PNG`);
      setLiveConnected(false);
      liveConnectedRef.current = false;
      setStreamMode('fallback');
    };
  }, [deviceId, initJMuxer]);

  const disconnectScrcpy = useCallback(() => {
    if (wsRef.current) {
      // Tell server to stop the scrcpy process immediately (don't wait grace period)
      try {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'stop' }));
        }
        wsRef.current.close();
      } catch (_) {}
      wsRef.current = null;
    }
    if (jmuxerRef.current) {
      try { jmuxerRef.current.destroy(); } catch (_) {}
      jmuxerRef.current = null;
    }
    setLiveConnected(false);
    liveConnectedRef.current = false;
  }, []);

  // Send scrcpy control message over WebSocket
  const sendScrcpyControl = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    }
  }, []);

  // Connect/disconnect scrcpy based on mode and device
  useEffect(() => {
    if (!isOpen || !deviceId || !streaming) {
      disconnectScrcpy();
      return;
    }
    if (streamMode === 'live') {
      connectScrcpy();
    } else {
      disconnectScrcpy();
    }
    return () => { disconnectScrcpy(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamMode, isOpen, deviceId, streaming]);

  // ════════════════════════════════════════════════
  // FALLBACK: PNG FRAME CAPTURE (sequential polling)
  // Only active when streamMode === 'fallback'
  // ════════════════════════════════════════════════
  const fetchFrame = useCallback(async () => {
    if (!deviceId || !mountedRef.current) return;
    // Skip this cycle if paused for command, but reschedule
    if (commandPauseRef.current) return;
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const res = await fetch(`/api/screenshot/${deviceId}`, { cache: 'no-store' });
      // 404 = device not found → stop immediately, no retry
      if (res.status === 404) {
        if (mountedRef.current) {
          setError('Device déconnecté (non trouvé via ADB)');
          if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
          setStreaming(false);
        }
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Only accept real PNG frames, not SVG error placeholders
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('image/png')) throw new Error('Pas de capture disponible');
      const blob = await res.blob();
      if (!mountedRef.current) return;
      const url = URL.createObjectURL(blob);
      if (imgRef.current) {
        const oldSrc = imgRef.current.src;
        imgRef.current.src = url;
        if (oldSrc && oldSrc.startsWith('blob:')) URL.revokeObjectURL(oldSrc);
      }
      setHasFrame(true);
      setError(null);
      errorCountRef.current = 0;
      frameCountRef.current++;
    } catch (e) {
      errorCountRef.current++;
      if (mountedRef.current) {
        setError(e.message);
        if (errorCountRef.current >= 15) {
          if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
          setStreaming(false);
          return; // Don't reschedule
        }
      }
    } finally {
      fetchingRef.current = false;
    }
  }, [deviceId]);

  // Sequential poll loop: waits for fetch to complete, then schedules next (fallback only)
  useEffect(() => {
    if (!isOpen || !deviceId || !streaming || streamMode !== 'fallback') return;
    mountedRef.current = true;
    errorCountRef.current = 0;

    const delay = mode === 'video' ? pollIntervalMs : Math.max(pollIntervalMs * 2, 800);
    let cancelled = false;

    const pollLoop = async () => {
      if (cancelled || !mountedRef.current) return;
      await fetchFrame();
      if (cancelled || !mountedRef.current) return;
      // Schedule next fetch only after current one is done
      pollTimerRef.current = setTimeout(pollLoop, delay);
    };

    pollLoop();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, isOpen, deviceId, streaming, pollIntervalMs, streamMode]);

  // Fetch device stats (battery, cpu, temp, ram)
  const fetchStats = useCallback(async () => {
    if (!deviceId || !mountedRef.current) return;
    try {
      const r = await fetch(`/api/device-stats/${deviceId}`);
      if (r.ok) {
        const data = await r.json();
        if (mountedRef.current) setDeviceStats(data);
      }
    } catch (e) { /* silent */ }
  }, [deviceId]);

  // Stats polling
  useEffect(() => {
    if (statsTimerRef.current) clearInterval(statsTimerRef.current);
    if (isOpen && deviceId) {
      fetchStats();
      statsTimerRef.current = setInterval(fetchStats, STATS_POLL_INTERVAL);
    }
    return () => { if (statsTimerRef.current) clearInterval(statsTimerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, deviceId]);

  // FPS counter
  useEffect(() => {
    const iv = setInterval(() => {
      setFps(frameCountRef.current);
      frameCountRef.current = 0;
    }, 1000);
    return () => clearInterval(iv);
  }, []);

  // Reset state on device change + fetch screen resolution
  useEffect(() => {
    setHasFrame(false);
    setError(null);
    setStreaming(true);
    setDeviceStats(null);
    setStreamMode('live'); // Try scrcpy live first for each new device
    setLiveConnected(false);
    setMode('video');
    if (deviceId && isOpen) {
      (async () => {
        try {
          const r = await fetch(`/api/devices/${deviceId}/info`);
          if (r.ok) {
            const data = await r.json();
            const s = data.screen || data;
            if (s.width && s.height) setScreenRes({ w: s.width, h: s.height });
          }
        } catch (e) { /* use defaults */ }
      })();
    }
  }, [deviceId, isOpen]);

  const toggleStream = () => setStreaming(prev => !prev);

  // ════════════════════════════════════════════════
  // TOUCH / GESTURE HANDLING (pointer events)
  // In live mode: sends scrcpy touch events (instant, no ADB overhead)
  // In fallback mode: sends ADB input commands via HTTP
  // ════════════════════════════════════════════════
  const handlePointerDown = useCallback((e) => {
    if (!deviceId) return;
    e.preventDefault();
    const dev = pointerToDevice(e.clientX, e.clientY);
    if (!dev) return;
    pointerDownRef.current = { ...dev, clientX: e.clientX, clientY: e.clientY, time: Date.now() };

    if (streamMode === 'live' && liveConnectedRef.current) {
      // Scrcpy: send touch down immediately
      sendScrcpyControl({ type: 'touch', action: 0, x: dev.x, y: dev.y, width: screenRes.w, height: screenRes.h });
    } else {
      // Fallback: start long-press timer for ADB
      longPressTimerRef.current = setTimeout(() => {
        if (pointerDownRef.current) {
          adbCommand('swipe', { x1: dev.x, y1: dev.y, x2: dev.x, y2: dev.y, duration: 800 });
          pointerDownRef.current = null;
        }
      }, LONG_PRESS_MS);
    }
    if (phoneFrameRef.current) phoneFrameRef.current.setPointerCapture(e.pointerId);
  }, [deviceId, pointerToDevice, adbCommand, streamMode, sendScrcpyControl, screenRes]);

  const handlePointerMove = useCallback((e) => {
    if (!pointerDownRef.current || !deviceId) return;
    if (streamMode === 'live' && liveConnectedRef.current) {
      const dev = pointerToDevice(e.clientX, e.clientY, true);
      if (!dev) return;
      sendScrcpyControl({ type: 'touch', action: 2, x: dev.x, y: dev.y, width: screenRes.w, height: screenRes.h });
    }
  }, [deviceId, pointerToDevice, streamMode, sendScrcpyControl, screenRes]);

  const handlePointerUp = useCallback((e) => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    const down = pointerDownRef.current;
    if (!down || !deviceId) { pointerDownRef.current = null; return; }

    const upDev = pointerToDevice(e.clientX, e.clientY, true);
    pointerDownRef.current = null;
    if (!upDev) return;

    if (streamMode === 'live' && liveConnectedRef.current) {
      // Scrcpy: send touch up — scrcpy handles tap vs swipe natively
      sendScrcpyControl({ type: 'touch', action: 1, x: upDev.x, y: upDev.y, width: screenRes.w, height: screenRes.h });
    } else {
      // Fallback: detect tap vs swipe
      const dx = e.clientX - down.clientX;
      const dy = e.clientY - down.clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const elapsed = Date.now() - down.time;

      if (dist < SWIPE_MIN_DIST) {
        adbCommand('tap', { x: down.x, y: down.y });
      } else {
        const duration = Math.max(100, Math.min(elapsed, 800));
        adbCommand('swipe', { x1: down.x, y1: down.y, x2: upDev.x, y2: upDev.y, duration });
      }
      if (mode === 'screenshot') setTimeout(fetchFrame, 300);
    }
  }, [deviceId, pointerToDevice, adbCommand, mode, fetchFrame, streamMode, sendScrcpyControl, screenRes]);

  const handlePointerCancel = useCallback(() => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    pointerDownRef.current = null;
  }, []);

  // ════════════════════════════════════════════════
  // BUTTON / ACTION HANDLERS
  // In live mode: instant via scrcpy control socket
  // In fallback mode: ADB shell commands via HTTP
  // ════════════════════════════════════════════════
  const sendButton = useCallback((action) => {
    if (!deviceId) return;
    if (streamMode === 'live' && liveConnectedRef.current) {
      sendScrcpyControl({ type: 'button', action });
    } else {
      adbCommand('button', { action });
      setTimeout(fetchFrame, 500);
    }
  }, [deviceId, adbCommand, fetchFrame, streamMode, sendScrcpyControl]);

  const sendSwipe = useCallback((direction) => {
    if (!deviceId) return;
    const mid = Math.round(screenRes.w / 2);
    const h = screenRes.h;
    let x1, y1, x2, y2;
    if (direction === 'down') {
      x1 = mid; y1 = Math.round(h * 0.4); x2 = mid; y2 = Math.round(h * 0.8);
    } else {
      x1 = mid; y1 = Math.round(h * 0.8); x2 = mid; y2 = Math.round(h * 0.4);
    }
    if (streamMode === 'live' && liveConnectedRef.current) {
      // Simulate swipe via scrcpy: touch down → move → up
      sendScrcpyControl({ type: 'touch', action: 0, x: x1, y: y1, width: screenRes.w, height: screenRes.h });
      setTimeout(() => {
        sendScrcpyControl({ type: 'touch', action: 2, x: x2, y: y2, width: screenRes.w, height: screenRes.h });
        setTimeout(() => {
          sendScrcpyControl({ type: 'touch', action: 1, x: x2, y: y2, width: screenRes.w, height: screenRes.h });
        }, 50);
      }, 250);
    } else {
      adbCommand('swipe', { x1, y1, x2, y2, duration: 300 });
      setTimeout(fetchFrame, 600);
    }
  }, [deviceId, screenRes, adbCommand, fetchFrame, streamMode, sendScrcpyControl]);

  const sendNotifPanel = useCallback(() => {
    if (streamMode === 'live' && liveConnectedRef.current) {
      sendScrcpyControl({ type: 'touch', action: 0, x: 360, y: 0, width: screenRes.w, height: screenRes.h });
      setTimeout(() => {
        sendScrcpyControl({ type: 'touch', action: 2, x: 360, y: 800, width: screenRes.w, height: screenRes.h });
        setTimeout(() => {
          sendScrcpyControl({ type: 'touch', action: 1, x: 360, y: 800, width: screenRes.w, height: screenRes.h });
        }, 50);
      }, 250);
    } else {
      adbCommand('swipe', { x1: 360, y1: 0, x2: 360, y2: 800, duration: 300 });
      setTimeout(fetchFrame, 700);
    }
  }, [adbCommand, fetchFrame, streamMode, sendScrcpyControl, screenRes]);

  const [airplaneToggling, setAirplaneToggling] = useState(false);
  const [tetheringToggling, setTetheringToggling] = useState(false);
  const [tetheringOn, setTetheringOn] = useState(false);
  const toggleAirplaneMode = useCallback(async () => {
    if (!deviceId || airplaneToggling) return;
    setAirplaneToggling(true);
    try {
      // Enable airplane mode
      await fetch('/api/device-action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_id: deviceId, action: 'airplane-on' }),
      });
      // Wait 3s then disable
      await new Promise(r => setTimeout(r, 3000));
      await fetch('/api/device-action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_id: deviceId, action: 'airplane-off' }),
      });
    } catch (e) {
      console.warn('Airplane mode toggle error:', e.message);
    } finally {
      setAirplaneToggling(false);
      if (streamMode === 'fallback') setTimeout(fetchFrame, 1000);
    }
  }, [deviceId, airplaneToggling, streamMode, fetchFrame]);

  const toggleTethering = useCallback(async () => {
    if (!deviceId || tetheringToggling) return;
    setTetheringToggling(true);
    const action = tetheringOn ? 'tethering-off' : 'tethering-on';
    try {
      await fetch('/api/device-action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_id: deviceId, action }),
      });
      setTetheringOn(!tetheringOn);
    } catch (e) {
      console.warn('Tethering toggle error:', e.message);
    } finally {
      setTetheringToggling(false);
    }
  }, [deviceId, tetheringToggling, tetheringOn]);

  const rebootDevice = async () => {
    if (!deviceId) return;
    if (!window.confirm(`Redémarrer ${deviceName} ?`)) return;
    try {
      await fetch(`/api/devices/${deviceId}/reboot`, { method: 'POST' });
    } catch (e) { /* silent */ }
  };

  const stopScript = async () => {
    if (!deviceId || stopping) return;
    setStopping(true);
    try {
      const r = await fetch('/api/stop-script-graceful', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_id: deviceId })
      });
      const data = await r.json();
      if (!data.success) console.warn('Stop script:', data.error || 'failed');
    } catch (e) { console.error('Stop script error:', e); }
    finally { setTimeout(() => setStopping(false), 2000); }
  };

  const sendText = useCallback(() => {
    if (!deviceId || !textInput.trim()) return;
    if (streamMode === 'live' && liveConnectedRef.current) {
      sendScrcpyControl({ type: 'text', text: textInput.trim() });
    } else {
      adbCommand('text', { text: textInput.trim() });
      setTimeout(fetchFrame, 500);
    }
    setTextInput('');
  }, [deviceId, textInput, adbCommand, fetchFrame, streamMode, sendScrcpyControl]);

  const bat = deviceStats?.battery ?? '—';
  const temp = deviceStats?.temperature ?? '—';
  const cpu = deviceStats?.cpu ?? '—';
  const ram = deviceStats?.ram ?? '—';

  return (
    <Panel $open={isOpen} $rightOffset={rightOffset} $wide={wideMode}>
      <PanelHeader>
        <PanelTitle>
          <Monitor size={13} /> Screen Mirror
        </PanelTitle>
        <HeaderBtn $active={streaming} onClick={toggleStream} title={streaming ? 'Pause' : 'Play'}>
          {streaming ? <Pause size={13} /> : <Play size={13} />}
        </HeaderBtn>
        <HeaderBtn
          $active={streamMode === 'live'}
          onClick={() => setStreamMode(m => m === 'live' ? 'fallback' : 'live')}
          title={streamMode === 'live' ? 'Mode: Live (scrcpy)' : 'Mode: Fallback (PNG)'}
        >
          <Zap size={12} />
        </HeaderBtn>
        <HeaderBtn onClick={() => {
          if (streamMode === 'fallback') { setHasFrame(false); setError(null); fetchFrame(); }
          else { disconnectScrcpy(); setTimeout(() => connectScrcpy(), 200); }
        }} title="Rafraîchir">
          <RefreshCw size={12} />
        </HeaderBtn>
        <HeaderBtn $active={wideMode} onClick={() => setWideMode(w => { const next = !w; onWideModeChange?.(next); return next; })} title={wideMode ? 'Mode compact' : 'Mode large'}>
          {wideMode ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
        </HeaderBtn>
        <HeaderBtn onClick={onClose} title="Fermer">
          <X size={13} />
        </HeaderBtn>
      </PanelHeader>

      {/* ── Phone screen + scroll buttons on right ── */}
      <ScreenArea $expanded={panelExpanded}>
        {deviceId ? (
          <>
            <ScreenColumn>
              <PhoneFrame
                ref={phoneFrameRef}
                $wide={wideMode}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerCancel}
                style={{ touchAction: 'none' }}
              >
                {/* Live mode: H.264 video via scrcpy */}
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  style={{
                    width: '100%', height: '100%', objectFit: 'contain',
                    display: (streamMode === 'live' && liveConnected) ? 'block' : 'none',
                    pointerEvents: 'none',
                  }}
                />
                {/* Fallback mode: PNG screenshots */}
                <ScreenImg
                  ref={imgRef}
                  alt=""
                  draggable={false}
                  style={{ display: (streamMode === 'fallback' && hasFrame) ? 'block' : 'none' }}
                />
                {/* Placeholder shown when no frame yet */}
                {!((streamMode === 'live' && liveConnected) || (streamMode === 'fallback' && hasFrame)) && (
                  <Placeholder>
                    {error ? (
                      <>
                        <AlertCircle size={24} />
                        <span>Erreur {streamMode === 'live' ? 'scrcpy' : 'Screenshot'}<br /><small>{error}</small></span>
                      </>
                    ) : (
                      <>
                        <Smartphone size={28} />
                        <span>{streaming
                          ? (streamMode === 'live' ? 'Connexion scrcpy...' : 'Chargement...')
                          : 'En pause'}</span>
                      </>
                    )}
                  </Placeholder>
                )}
              </PhoneFrame>
            </ScreenColumn>
            <ScrollColumn>
              <ScrollBtn onClick={() => sendSwipe('up')} title="Scroll haut"><ArrowUp size={14} /></ScrollBtn>
              <ScrollBtn onClick={() => sendSwipe('down')} title="Scroll bas"><ArrowDown size={14} /></ScrollBtn>
            </ScrollColumn>
          </>
        ) : (
          <Placeholder style={{ position: 'static', padding: '40px 20px' }}>
            <Smartphone size={36} />
            <span>Sélectionnez un appareil<br />pour voir son écran</span>
          </Placeholder>
        )}
      </ScreenArea>

      {/* ── Control panel ── */}
      <ControlPanel $expanded={panelExpanded}>
        {/* Always visible: device identity + action buttons + nav */}
        <ControlTop>
          <DeviceRow>
            <DeviceIdentity>
              {profilePicUrl ? (
                <ProfilePic
                  src={profilePicUrl}
                  alt=""
                  onError={e => {
                    const step = parseInt(e.target.dataset.fallback || '0');
                    if (step === 0 && serverPicUrl) { e.target.dataset.fallback = '1'; e.target.src = serverPicUrl; }
                    else if (step <= 1 && accountUsername) { e.target.dataset.fallback = '2'; e.target.src = `https://unavatar.io/instagram/${accountUsername}`; }
                    else { e.target.style.display = 'none'; }
                  }}
                />
              ) : (
                <Smartphone size={14} style={{ color: 'var(--primary)', flexShrink: 0 }} />
              )}
              <DeviceInfo>
                <DeviceNameSmall>{deviceName}</DeviceNameSmall>
                {accountUsername && <AccountNameSmall>@{accountUsername}</AccountNameSmall>}
              </DeviceInfo>
            </DeviceIdentity>
            <ActionBtns>
              <ActionBtn
                $color="rgba(34,197,94,.1)" $iconColor="#22c55e" $hoverBg="#22c55e"
                onClick={toggleStream}
                title={streaming ? 'Pause stream' : 'Play stream'}
              >
                {streaming ? <Pause size={13} /> : <Play size={13} />}
              </ActionBtn>
              <ActionBtn
                $color="rgba(239,68,68,.1)" $iconColor="#ef4444" $hoverBg="#ef4444"
                onClick={stopScript}
                disabled={stopping}
                title="Arrêter le script"
              >
                <Square size={12} />
              </ActionBtn>
              <ActionBtn
                $color={showTextInput ? 'var(--primary)' : 'var(--surface)'}
                $iconColor={showTextInput ? '#fff' : 'var(--text-muted)'}
                onClick={() => setShowTextInput(prev => !prev)}
                title="Saisir du texte"
              >
                <Type size={13} />
              </ActionBtn>
            </ActionBtns>
            <StatusBadge $on={(streamMode === 'live' ? liveConnected : hasFrame) && streaming}>
              {streamMode === 'live'
                ? (liveConnected ? 'LIVE' : error ? 'ERR' : '...')
                : (hasFrame ? 'PNG' : error ? 'ERR' : '...')}
            </StatusBadge>
          </DeviceRow>

          {/* Text input bar (togglable) */}
          {showTextInput && deviceId && (
            <TextInputBar>
              <TextInputField
                value={textInput}
                onChange={e => setTextInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') sendText(); }}
                placeholder="Saisir du texte..."
                autoFocus
              />
              <TextSendBtn onClick={sendText} title="Envoyer">
                <Send size={12} />
              </TextSendBtn>
            </TextInputBar>
          )}

          {deviceId && (
            <NavSection>
              <NavBtn onClick={() => sendButton('back')} title="Retour"><ChevronLeft size={12} /></NavBtn>
              <NavBtn onClick={() => sendButton('home')} title="Home"><Home size={12} /></NavBtn>
              <NavBtn onClick={() => sendButton('recent')} title="Apps récentes"><LayoutGrid size={12} /></NavBtn>
              <NavBtn onClick={sendNotifPanel} title="Notifications"><Bell size={12} /></NavBtn>
              <NavBtn onClick={toggleAirplaneMode} title={airplaneToggling ? 'Mode avion en cours...' : 'Toggle mode avion'} style={airplaneToggling ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                <Plane size={12} />
              </NavBtn>
              <NavBtn onClick={toggleTethering} title={tetheringToggling ? 'Partage en cours...' : (tetheringOn ? 'Désactiver le partage de connexion' : 'Activer le partage de connexion')}
                style={{ ...(tetheringToggling ? { opacity: 0.5, pointerEvents: 'none' } : {}), ...(tetheringOn ? { background: 'rgba(34,197,94,.2)', color: '#22c55e' } : {}) }}>
                <Wifi size={12} />
              </NavBtn>
              <RestartBtn onClick={rebootDevice} title="Redémarrer"><RotateCcw size={12} /></RestartBtn>
            </NavSection>
          )}
        </ControlTop>

        {/* Toggle expand/collapse */}
        <ExpandToggle onClick={() => setPanelExpanded(prev => !prev)}>
          {panelExpanded ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
          {panelExpanded ? 'Masquer détails' : 'Afficher détails'}
        </ExpandToggle>

        {/* Expandable: stats + device info */}
        {panelExpanded && (
          <DetailsSection>
            {/* Stats grid */}
            {deviceId && (
              <StatsGrid>
                <StatCard>
                  <StatIcon $color="rgba(34,197,94,.1)" $iconColor="#22c55e">
                    {bat !== '—' && bat > 50 ? <Battery size={14} /> : <BatteryCharging size={14} />}
                  </StatIcon>
                  <StatInfo>
                    <StatValue>{bat !== '—' ? `${bat}%` : '—'}</StatValue>
                    <StatLabel>Batterie</StatLabel>
                  </StatInfo>
                </StatCard>

                <StatCard>
                  <StatIcon $color="rgba(239,68,68,.1)" $iconColor="#ef4444">
                    <Thermometer size={14} />
                  </StatIcon>
                  <StatInfo>
                    <StatValue>{temp !== '—' ? `${temp}°C` : '—'}</StatValue>
                    <StatLabel>Temp.</StatLabel>
                  </StatInfo>
                </StatCard>

                <StatCard>
                  <StatIcon $color="rgba(59,130,246,.1)" $iconColor="#3b82f6">
                    <Cpu size={14} />
                  </StatIcon>
                  <StatInfo>
                    <StatValue>{cpu !== '—' ? `${cpu}%` : '—'}</StatValue>
                    <StatLabel>CPU</StatLabel>
                  </StatInfo>
                </StatCard>

                <StatCard>
                  <StatIcon $color="rgba(168,85,247,.1)" $iconColor="#a855f7">
                    <HardDrive size={14} />
                  </StatIcon>
                  <StatInfo>
                    <StatValue>{ram !== '—' ? `${ram}%` : '—'}</StatValue>
                    <StatLabel>RAM</StatLabel>
                  </StatInfo>
                </StatCard>
              </StatsGrid>
            )}

            {/* Device ID */}
            {deviceId && (
              <InfoRow>
                <InfoIcon $bg="rgba(165,145,239,.1)" $color="var(--primary)">
                  <Fingerprint size={13} />
                </InfoIcon>
                <InfoText>
                  <InfoLabel>Device ID</InfoLabel>
                  <InfoValue>{deviceId}</InfoValue>
                </InfoText>
              </InfoRow>
            )}

            {/* IP Address */}
            {deviceIp && (
              <InfoRow>
                <InfoIcon $bg="rgba(59,130,246,.1)" $color="#3b82f6">
                  <Wifi size={13} />
                </InfoIcon>
                <InfoText>
                  <InfoLabel>Adresse IP</InfoLabel>
                  <InfoValue>{deviceIp}</InfoValue>
                </InfoText>
              </InfoRow>
            )}
          </DetailsSection>
        )}
      </ControlPanel>

      {/* ── Slim status bar ── */}
      <StatusBar>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <StatusDot $on={(streamMode === 'live' ? liveConnected : hasFrame) && streaming} />
          <span>{streaming ? `${fps} FPS` : 'Pause'}</span>
          {streaming && streamMode === 'fallback' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginLeft: 4 }}>
              <button onClick={() => { const v = Math.min(pollIntervalMs + 100, 2000); setPollIntervalMs(v); localStorage.setItem('mirrorPollInterval', v); }} style={{ background: 'rgba(239,68,68,.2)', color: '#ef4444', border: 'none', borderRadius: 3, width: 16, height: 14, fontSize: 9, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }} title="Slower (higher interval)">−</button>
              <span style={{ fontSize: 8, fontWeight: 700, color: 'var(--text-muted)', minWidth: 28, textAlign: 'center' }}>{pollIntervalMs}ms</span>
              <button onClick={() => { const v = Math.max(pollIntervalMs - 100, 100); setPollIntervalMs(v); localStorage.setItem('mirrorPollInterval', v); }} style={{ background: 'rgba(34,197,94,.2)', color: '#22c55e', border: 'none', borderRadius: 3, width: 16, height: 14, fontSize: 9, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }} title="Faster (lower interval)">+</button>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            fontSize: 8, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
            background: streamMode === 'live'
              ? (liveConnected ? 'rgba(255,56,56,.15)' : 'rgba(255,255,255,.1)')
              : (mode === 'video' ? 'rgba(34,197,94,.15)' : 'rgba(245,158,11,.15)'),
            color: streamMode === 'live'
              ? (liveConnected ? '#ff3838' : '#888')
              : (mode === 'video' ? '#22c55e' : '#f59e0b'),
            textTransform: 'uppercase', letterSpacing: '.3px', cursor: 'pointer'
          }} onClick={() => {
            if (streamMode === 'live') setStreamMode('fallback');
            else if (streamMode === 'fallback') setMode(m => m === 'video' ? 'screenshot' : 'video');
          }} title={streamMode === 'live' ? 'Click to switch to fallback' : 'Click to toggle video/screenshot speed'}>
            {streamMode === 'live'
              ? (liveConnected ? '⚡ LIVE' : '⚡ CONNECTING...')
              : (mode === 'video' ? '▶ VIDEO' : '📷 SCREENSHOT')}
          </span>
          <span style={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 9 }}>
            {deviceId || '—'}
          </span>
        </div>
      </StatusBar>
    </Panel>
  );
};

export default ScreenMirrorPanel;
